import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Landmark,
  LayoutDashboard,
  BadgePercent,
  Bell,
  MessageSquare,
  LogOut,
  Menu,
  X,
  MessageCircle,
  Handshake,
  User,
} from 'lucide-react';
import { getUser } from '../services/auth';
import { authAPI } from '../services/api';
import { toast } from 'sonner';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { NotificationBell } from '../components/NotificationBell';
import { PostLoginFeaturePrompt } from '../components/PostLoginFeaturePrompt';
import { onboardingConfig } from '../config/onboardingConfig';
import { ThemeToggle } from '../components/ThemeToggle';
import { getDefaultAuthenticatedPath, getEnabledMenuDefinitions, isMenuEnabled } from '../config/navigation';
import { useLocalization } from '../context/LocalizationContext';

export const DashboardLayout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [showLogoutPrompt, setShowLogoutPrompt] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();
  const { t, tenantBrand } = useLocalization();
  const defaultAuthenticatedPath = getDefaultAuthenticatedPath();
  const feedbackEnabled = isMenuEnabled('feedback');

  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!showLogoutPrompt) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowLogoutPrompt(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLogoutPrompt]);

  const performLogout = async () => {
    await authAPI.logout();
    toast.success(t('layout.logoutSuccess', {}, 'Logged out successfully'));
    navigate('/login');
  };

  const handleLogoutRequest = () => {
    setMobileMenuOpen(false);
    if (!feedbackEnabled) {
      void performLogout();
      return;
    }
    setShowLogoutPrompt(true);
  };

  const handleFeedbackBeforeLogout = () => {
    setShowLogoutPrompt(false);
    navigate('/feedback');
  };

  const handleLogout = async () => {
    setShowLogoutPrompt(false);
    await performLogout();
  };

  const navItems = getEnabledMenuDefinitions().map((item) => ({
    ...item,
    label: t(item.labelKey),
    icon:
      item.id === 'dashboard'
        ? LayoutDashboard
        : item.id === 'offers'
          ? BadgePercent
        : item.id === 'surety'
          ? Handshake
          : item.id === 'services'
            ? MessageSquare
            : item.id === 'feedback'
              ? MessageCircle
              : User,
  }));

  const isActive = (path) => location.pathname === path;
  const currentYear = new Date().getFullYear();
  const renderNavLink = (item, mobile = false) => {
    const Icon = item.icon;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={mobile ? () => setMobileMenuOpen(false) : undefined}
        data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
        className={`flex items-center ${mobile ? 'space-x-3 px-4 py-3' : 'h-11 space-x-2 px-4 py-2.5'} rounded-xl transition-colors ${
          isActive(item.path)
            ? 'bg-white dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium shadow-sm ring-1 ring-primary-200/80'
            : 'text-primary-700 dark:text-primary-300 hover:bg-primary-50/80 dark:hover:bg-slate-800 hover:text-primary-900 dark:hover:text-white'
        }`}
      >
        <Icon size={mobile ? 20 : 18} strokeWidth={1.5} />
        <span className={`${mobile ? '' : 'whitespace-nowrap text-sm'} font-bold`}>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen app-page-shell-bg transition-colors">
      {onboardingConfig.postLoginFeaturePrompt && <PostLoginFeaturePrompt userName={user?.name} />}
      {showLogoutPrompt && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center px-3 py-3 sm:items-center sm:px-4 sm:py-6">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
            onClick={() => setShowLogoutPrompt(false)}
            aria-label={t('layout.dismissLogoutPrompt')}
          />
          <div className="app-modal-surface relative z-10 w-full max-w-md rounded-[1.5rem] p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary-600 dark:text-primary-300">
                  {t('layout.feedbackPromptKicker')}
                </p>
                <h2 className="app-panel-title mt-1">{t('layout.feedbackPromptTitle')}</h2>
                <p className="mt-2 text-sm leading-6 text-primary-700 dark:text-primary-200">
                  {t('layout.feedbackPromptSubtitle')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowLogoutPrompt(false)}
                className="rounded-full p-2 text-slate-500 transition-colors hover:bg-primary-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                aria-label={t('layout.dismissLogoutPrompt')}
              >
                <X size={18} />
              </button>
            </div>
            <div className="mt-5 rounded-2xl border border-primary-100 bg-primary-50/90 px-4 py-3 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-300">
              {t('layout.feedbackPromptNote')}
            </div>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleLogout}
                className="app-secondary-button justify-center"
              >
                {t('layout.logoutNow')}
              </button>
              <button
                type="button"
                onClick={handleFeedbackBeforeLogout}
                className="app-primary-button justify-center"
              >
                {t('layout.shareFeedback')}
              </button>
            </div>
          </div>
        </div>
      )}

      <nav className="bg-white/96 dark:bg-slate-900/95 border-b border-primary-200/80 dark:border-slate-800 sticky top-0 z-50 shadow-sm backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to={defaultAuthenticatedPath} className="flex items-center space-x-3">
                <div className="bg-primary-500 p-2.5 rounded-xl shadow-md">
                  <Landmark className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <span className="font-serif text-lg font-bold tracking-tight text-primary-700 dark:text-white hidden lg:block">{t('common.appName')}</span>
              </Link>
            </div>

            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => renderNavLink(item))}
            </div>

            <div className="flex items-center space-x-4">
              <LanguageSwitcher compact className="sm:hidden" />
              <LanguageSwitcher compact className="hidden sm:inline-flex" />
              <NotificationBell />
              <ThemeToggle compact />

              <button
                onClick={handleLogoutRequest}
                data-testid="logout-button"
                className="hidden md:flex items-center space-x-2 text-primary-700 dark:text-primary-300 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              >
                <LogOut size={18} />
                <span className="text-sm font-bold">{t('common.logout')}</span>
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden text-primary-900 dark:text-white"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-primary-200/80 dark:border-slate-800 bg-white/96 dark:bg-slate-900" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item) => renderNavLink(item, true))}
              <button
                onClick={handleLogoutRequest}
                className="flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 w-full transition-colors"
              >
                <LogOut size={20} />
                <span className="font-bold">{t('common.logout')}</span>
              </button>
            </div>
          </div>
        )}
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 sm:pt-4 pb-8">
        {children}
      </main>

      <footer className="border-t border-primary-100 dark:border-slate-800 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-center">
          <p className="text-xs sm:text-sm text-primary-600 dark:text-primary-300">
            © {currentYear} {tenantBrand.fullName}. {t('layout.footerRightsSuffix', {}, 'All rights reserved.')}
          </p>
          <p className="mt-1 text-xs sm:text-sm text-primary-600 dark:text-primary-300">
            {t('layout.poweredBy', {}, 'Powered by')}{' '}
            <a
              href="https://www.ykinnosoft.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-primary-600 underline decoration-primary-300 underline-offset-4 transition-colors hover:text-primary-900 hover:decoration-primary-500 dark:text-primary-300 dark:decoration-primary-500/80 dark:hover:text-white dark:hover:decoration-white"
            >
              {t('layout.companyName', {}, 'YK Innosoft Technologies')}
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};
